import React, { useEffect, useState } from 'react'
import Button from 'components/button'
import Spinner from 'components/spinner'
import Tooltip from 'components/tooltip'
import { Dependency, Expand, Grid } from 'components/icons'
import api from 'helpers/api'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import {
  keyComparisons,
  keysMatch,
  matchValues,
  previewValue
} from 'components/spreadsheet/components/modal/views/tablejoins'
import { useViewer } from 'hooks/useViewer'
import { ITableColumn, ITableJoin, ITableViewColumn } from 'types'

// How many matched values the sample shows before it fades out.
const SAMPLED_VALUES = 5
// The source rows it reads: enough to find the rows that share a value, never all of a large table.
const SAMPLE_ROWS = 500

interface LookupSampleProps {
  join: ITableJoin
  /** The lookup column, in this table, whose values are sampled. */
  columnId: string
  /** Opens the lookups at this source, from the View all pill. */
  onViewAll: () => void
}

/**
 * A lookup column's values as Edit column's Options shows a dependent select's: each value the rows are matched
 * on that a row here has, under the blue dependency icon, and below it the value a row with it takes, from the
 * source (with how many other values the matching rows have, when it takes the first). The first five, fading
 * out under View all.
 */
const LookupSample: React.FC<LookupSampleProps> = ({ join, columnId, onViewAll }) => {
  const { spreadsheetData } = useDataContext()
  const table = spreadsheetData.tableDetails
  const [sample, setSample] = useState<
    | {
        columns: ITableViewColumn[]
        rows: Array<Record<string, unknown>>
        ownRows: Array<Record<string, unknown>>
      }
    | null
    | undefined
  >(undefined)

  // Read again for another account (or a masqueraded one), as what each may see differs.
  const who = useViewer()
  useEffect(() => {
    let current = true
    setSample(undefined)
    Promise.resolve()
      .then(() =>
        Promise.all([
          api.getTableView(join.joinViewId, { ignoreCachedOptions: true }),
          api.getTableViewRowsPage(join.joinViewId, SAMPLE_ROWS),
          api.getTableRowsPage(table.publicId, SAMPLE_ROWS)
        ])
      )
      .then(([view, rows, ownRows]) => {
        if (current)
          setSample({
            columns: view.data.columns,
            rows: rows.data.map((row) => row.rowData),
            ownRows: ownRows.data.map((row) => row.rowData)
          })
      })
      .catch(() => current && setSample(null))
    return () => {
      current = false
    }
  }, [join.joinViewId, table.publicId, who])

  if (sample === undefined)
    return (
      <div className="flex h-8 items-center justify-center px-3">
        <Spinner className="size-4" />
      </div>
    )
  if (sample === null)
    return <p className="px-3 pb-1.5 text-muted">Its source can&apos;t be opened with your access.</p>

  const columnOf = (id: string) => sample.columns.find((column) => column.publicId === id)
  const rules = join.joinColumns
    .map((rule) => ({
      source: columnOf(rule.sourceColumnId),
      own: table.columns.find((column) => column.publicId === rule.targetColumnId)
    }))
    .filter((rule): rule is { source: ITableViewColumn; own: ITableColumn } => !!rule.source && !!rule.own)
  const keyColumns = rules.map((rule) => rule.source)
  const sourceId = join.dataColumns.find((data) => data.targetColumnId === columnId)?.sourceColumnId
  const valueColumn = sourceId ? columnOf(sourceId) : undefined
  if (!valueColumn || keyColumns.length === 0) return null

  // One line per set of values matched on, its rows in the source view's order: only those a row here has,
  // as the others land nowhere.
  const ownValues = sample.ownRows.map((row) => rules.map((rule) => matchValues(row[rule.own.name])))
  const comparisons = keyComparisons(rules, join.isOneToMany)
  const groups = new Map<string, Array<Record<string, unknown>>>()
  sample.rows.forEach((row) => {
    const matched = keyColumns.map((column) => matchValues(row[column.name]))
    if (!ownValues.some((own) => keysMatch(matched, own, comparisons))) return
    // By the values themselves, so a value "A, B" and a list of A and B are lines of their own.
    const key = JSON.stringify(matched)
    groups.set(key, [...(groups.get(key) ?? []), row])
  })
  const lines = [...groups.values()]
  if (lines.length === 0)
    return (
      <p className="px-3 pb-1.5 text-muted">
        No row here matches a row in {join.joinTableName} yet, so the column is empty.
      </p>
    )

  const valueOf = (rows: Array<Record<string, unknown>>): string =>
    join.isOneToMany
      ? [...new Set(rows.map((row) => previewValue(row[valueColumn.name])).filter(Boolean))].join(', ')
      : previewValue(rows[0][valueColumn.name])
  const othersOf = (rows: Array<Record<string, unknown>>): number => {
    if (join.isOneToMany) return 0
    const first = previewValue(rows[0][valueColumn.name])
    return new Set(
      rows
        .slice(1)
        .map((row) => previewValue(row[valueColumn.name]))
        .filter((value) => value !== '' && value !== first)
    ).size
  }

  return (
    <div className="relative flex flex-col gap-1 px-3 pb-1.5" data-testid="lookup-sample">
      <div
        className="flex flex-col gap-1"
        style={
          lines.length > SAMPLED_VALUES
            ? { maskImage: 'linear-gradient(to bottom, black 45%, transparent)' }
            : undefined
        }
      >
        {lines.slice(0, SAMPLED_VALUES).map((rows, index) => {
          const more = othersOf(rows)
          return (
            <div key={index} className="relative flex flex-col gap-1" data-testid="lookup-sample-line">
              {/* The value matched, under the dependency's blue icon, as a dependent select's group heading. */}
              <div className="flex h-7 min-w-0 items-center gap-1.5 text-[13px] text-body">
                <span className="flex shrink-0 text-blue [&_svg]:size-3.5">
                  <Dependency />
                </span>
                {/* As Options heads a dependent select's groups: "When Prospect and Entreprise", the values in
                    medium and the words between them muted. */}
                <span className="min-w-0 truncate">
                  <span className="text-muted">When </span>
                  {keyColumns.map((column, position) => (
                    <React.Fragment key={column.publicId}>
                      {position > 0 && (
                        <span className="text-muted">{position === keyColumns.length - 1 ? ' and ' : ', '}</span>
                      )}
                      <span className="font-medium">{previewValue(rows[0][column.name])}</span>
                    </React.Fragment>
                  ))}
                </span>
              </div>
              {/* The guide line runs down from under the icon, past the value it heads. */}
              <span aria-hidden className="absolute top-[25px] bottom-0 left-[6px] w-px bg-divider" />
              <div className="ml-[7px] pl-[13px]">
                <div className="flex h-8 items-center gap-2 rounded-md border border-divider bg-field pr-2 pl-3 text-[13px]">
                  <span className="min-w-0 truncate">{valueOf(rows)}</span>
                  {more > 0 && (
                    <Tooltip
                      content={`${more} other ${more === 1 ? 'value' : 'values'} in matching rows; each row takes the first.`}
                    >
                      <span className="shrink-0 text-muted">+{more} more</span>
                    </Tooltip>
                  )}
                  <Tooltip content={`From ${join.joinTableName}`}>
                    <span className="ml-auto flex shrink-0 text-blue [&_svg]:size-3.5">
                      <Grid />
                    </span>
                  </Tooltip>
                </div>
              </div>
            </div>
          )
        })}
      </div>
      {lines.length > SAMPLED_VALUES && (
        <div className="absolute inset-x-0 bottom-3 flex justify-center">
          <Button
            internalType="outline"
            className="h-7 gap-1.5 rounded-full border-divider bg-surface-raised px-3 text-[13px] font-medium text-body shadow-[0_1px_2px_rgb(0_0_0/0.06)] hover:bg-surface-hover [&_svg]:size-3.5 [&_svg]:text-muted"
            data-testid="lookup-sample-view-all"
            onClick={onViewAll}
          >
            View all ({lines.length.toLocaleString()})
            <Expand />
          </Button>
        </div>
      )}
    </div>
  )
}

export default LookupSample
